import { ApiResponse } from "@/utils/ApiResponse";
import type { QueryKey } from "@tanstack/vue-query";
import { useQueryClient, useMutation } from "@tanstack/vue-query";
import { useToast } from "vue-toastification";
import { MarcaProductoInterface } from "../../interfaces/marcaProductosInterfaces/MarcaProducto.interface";
import { ToggleVisibilityMarcaProductoAction } from "../../actions/marcaActions";

const toast = useToast();

type ToggleCtx = {
  previousEntries: [QueryKey, unknown][];
};

export function useMarcaProductoToggleVisib() {
  const queryClient = useQueryClient();

  const mutation = useMutation({
    mutationFn: (marcaId: string) =>
      ToggleVisibilityMarcaProductoAction(marcaId),

    onMutate: async (marcaId: string): Promise<ToggleCtx> => {
      await queryClient.cancelQueries({ queryKey: ["MarcaProductosList"] });
      const previousEntries = queryClient.getQueriesData({
        queryKey: ["MarcaProductosList"],
      });

      queryClient.setQueriesData<ApiResponse<MarcaProductoInterface>>(
        { queryKey: ["MarcaProductosList"] },
        (old) => {
          if (!old?.data?.data?.marcasProductos) return old;
          return {
            ...old,
            data: {
              ...old.data,
              data: {
                ...old.data.data,
                marcasProductos: old.data.data.marcasProductos.map((marca) =>
                  marca.id === marcaId
                    ? { ...marca, isVisible: marca.isVisible === 1 ? 0 : 1 }
                    : marca
                ),
              },
            },
          };
        }
      );

      return { previousEntries };
    },

    onError: (_error, _marcaId, context) => {
      context?.previousEntries?.forEach(([queryKey, data]) => {
        queryClient.setQueryData(queryKey, data);
      });
      toast.error("No se pudo actualizar la visibilidad de la marca.");
    },

    onSuccess: () => {
      toast.success("Visibilidad de marca de producto actualizada.");
    },

    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ["MarcaProductosList"] });
    },
  });

  return {
    ...mutation,
  };
}
